import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import {
  ShareIcon,
  GlobeAltIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ArrowPathIcon,
  TrashIcon,
  ClockIcon,
  StarIcon,
  LightBulbIcon,
  MagnifyingGlassIcon,
  LinkIcon,
  UserGroupIcon,
  HashtagIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';

const SocialMediaInsights = () => {
  const [domain, setDomain] = useState('');
  const [profiles, setProfiles] = useState({
    twitter: '',
    facebook: '',
    instagram: '',
    linkedin: '',
    youtube: '',
    tiktok: ''
  });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [showHistory, setShowHistory] = useState(false);

  const fetchHistory = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/social-media/history', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(res.data.analyses || []);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
    const loadLatest = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('/api/social-media/latest', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.analysis) {
          setResults(res.data.analysis);
          setDomain(res.data.analysis.domain || '');
        }
      } catch (err) {
        console.error('No previous analysis found');
      }
    };
    loadLatest();
  }, [fetchHistory]);

  const handleAnalyze = async (e) => {
    e.preventDefault();
    if (!domain.trim()) {
      setError('Please enter a domain');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post('/api/social-media/analyze', {
        domain: domain.trim(),
        profiles
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(res.data.analysis);
      fetchHistory();
    } catch (err) {
      setError(err.response?.data?.error || 'Analysis failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalysis = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/social-media/results/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(res.data.analysis);
      setDomain(res.data.analysis.domain || '');
      setShowHistory(false);
    } catch (err) {
      setError('Failed to load analysis');
    }
  };

  const deleteAnalysis = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/social-media/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchHistory();
      if (results && results.id === id) setResults(null);
    } catch (err) {
      setError('Failed to delete analysis');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    if (score >= 40) return 'text-orange-400';
    return 'text-red-400';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-green-500/20 border-green-500/30';
    if (score >= 60) return 'bg-yellow-500/20 border-yellow-500/30';
    if (score >= 40) return 'bg-orange-500/20 border-orange-500/30';
    return 'bg-red-500/20 border-red-500/30';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Needs Work';
    return 'Poor';
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'high': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'low': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const platformIcons = {
    twitter: <HashtagIcon className="w-5 h-5" />,
    facebook: <UserGroupIcon className="w-5 h-5" />,
    instagram: <PhotoIcon className="w-5 h-5" />,
    linkedin: <LinkIcon className="w-5 h-5" />,
    youtube: <ShareIcon className="w-5 h-5" />,
    tiktok: <StarIcon className="w-5 h-5" />
  };

  const platformColors = {
    twitter: 'from-blue-500 to-blue-600',
    facebook: 'from-blue-600 to-blue-700',
    instagram: 'from-pink-500 to-purple-600',
    linkedin: 'from-blue-700 to-blue-800',
    youtube: 'from-red-500 to-red-600',
    tiktok: 'from-gray-800 to-black'
  };

  const catScores = results?.categoryScores || {}; const scores = { profileCompleteness: catScores.presence || 0, contentQuality: catScores.consistency || 0, engagementPotential: catScores.engagement || 0, seoAlignment: catScores.seoAlignment || 0 };
  const platformScores = Object.fromEntries(Object.entries(results?.platforms || {}).map(([k,v]) => [v.name || k, { score: v.score || 0, metrics: v.metrics || {}, connected: v.connected, url: v.url, handle: v.handle, source: v.source }]));
  const recommendations = results?.recommendations || [];

  return (
    <div className="min-h-screen bg-[#0B1120] text-white">
      <Helmet>
        <title>Social Media Insights | SERPfinity</title>
        <meta name="description" content="Analyze your social media presence and get AI-powered recommendations to improve your social media strategy." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Social Media Insights
            </h1>
            <p className="text-gray-400 mt-2">Analyze and optimize your social media presence across platforms</p>
          </div>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 px-4 py-2 bg-[#1a2236] border border-gray-700 rounded-lg hover:bg-[#1e2a42] transition-colors"
          >
            <ClockIcon className="w-5 h-5 text-gray-400" />
            <span className="text-gray-300">History ({history.length})</span>
          </button>
        </div>

        {/* History Panel */}
        {showHistory && (
          <div className="mb-8 bg-[#111827] border border-gray-700/50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Analysis History</h3>
            {history.length === 0 ? (
              <p className="text-gray-400">No previous analyses found.</p>
            ) : (
              <div className="space-y-3">
                {history.map(item => (
                  <div key={item.id} className="flex items-center justify-between bg-[#1a2236] rounded-lg p-4 border border-gray-700/50">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getScoreBg(item.overallScore)}`}>
                        <span className={`text-sm font-bold ${getScoreColor(item.overallScore)}`}>{item.overallScore}</span>
                      </div>
                      <div>
                        <p className="font-medium text-white">{item.domain}</p>
                        <p className="text-sm text-gray-400">{new Date(item.createdAt).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => loadAnalysis(item.id)} className="px-3 py-1.5 bg-purple-600/20 text-purple-400 rounded-lg hover:bg-purple-600/30 text-sm">
                        View
                      </button>
                      <button onClick={() => deleteAnalysis(item.id)} className="p-1.5 text-gray-500 hover:text-red-400 transition-colors">
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Analysis Form */}
        <div className="bg-[#111827] border border-gray-700/50 rounded-xl p-6 mb-8">
          <form onSubmit={handleAnalyze}>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">Website Domain</label>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <GlobeAltIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="text"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    placeholder="example.com"
                    className="w-full pl-10 pr-4 py-3 bg-[#1a2236] border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all"
                >
                  {loading ? (
                    <>
                      <ArrowPathIcon className="w-5 h-5 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <MagnifyingGlassIcon className="w-5 h-5" />
                      Analyze
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Social Profile URLs */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.keys(profiles).map(platform => (
                <div key={platform}>
                  <label className="block text-xs font-medium text-gray-400 mb-1 capitalize">{platform} URL (optional)</label>
                  <input
                    type="text"
                    value={profiles[platform]}
                    onChange={(e) => setProfiles({...profiles, [platform]: e.target.value})}
                    placeholder={`${platform}.com/yourpage`}
                    className="w-full px-3 py-2 bg-[#1a2236] border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:ring-1 focus:ring-purple-500 focus:border-transparent outline-none"
                  />
                </div>
              ))}
            </div>
          </form>
          {error && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-400" />
              <span className="text-red-400 text-sm">{error}</span>
            </div>
          )}
        </div>

        {/* Results */}
        {results && results.status === 'completed' && (
          <>
            {/* Tab Navigation */}
            <div className="flex gap-1 mb-6 bg-[#111827] rounded-lg p-1 border border-gray-700/50">
              {[
                { id: 'overview', label: 'Overview', icon: ChartBarIcon },
                { id: 'platforms', label: 'Platforms', icon: ShareIcon },
                { id: 'recommendations', label: 'Recommendations', icon: LightBulbIcon }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-[#1a2236]'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Overall Score */}
                <div className="bg-[#111827] border border-gray-700/50 rounded-xl p-8 text-center">
                  <h2 className="text-lg font-semibold text-gray-300 mb-4">Overall Social Media Score</h2>
                  <div className={`inline-flex items-center justify-center w-32 h-32 rounded-full border-4 ${getScoreBg(results.overallScore)}`}>
                    <div>
                      <span className={`text-4xl font-bold ${getScoreColor(results.overallScore)}`}>{results.overallScore}</span>
                      <p className={`text-sm ${getScoreColor(results.overallScore)}`}>{getScoreLabel(results.overallScore)}</p>
                    </div>
                  </div>
                  <p className="text-gray-400 mt-4 max-w-md mx-auto">
                    Analysis for <span className="text-white font-medium">{results.domain}</span> completed on {new Date(results.createdAt).toLocaleDateString()}
                  </p>
                </div>

                {/* Category Scores */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { key: 'profileCompleteness', label: 'Profile Completeness', icon: UserGroupIcon, desc: 'How complete your profiles are' },
                    { key: 'contentQuality', label: 'Content Quality', icon: StarIcon, desc: 'Quality and relevance of content' },
                    { key: 'engagementPotential', label: 'Engagement', icon: ArrowTrendingUpIcon, desc: 'Potential for audience engagement' },
                    { key: 'seoAlignment', label: 'SEO Alignment', icon: MagnifyingGlassIcon, desc: 'Alignment with SEO strategy' }
                  ].map(cat => (
                    <div key={cat.key} className="bg-[#111827] border border-gray-700/50 rounded-xl p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 bg-purple-600/20 rounded-lg">
                          <cat.icon className="w-5 h-5 text-purple-400" />
                        </div>
                        <div>
                          <h3 className="font-medium text-white text-sm">{cat.label}</h3>
                          <p className="text-xs text-gray-500">{cat.desc}</p>
                        </div>
                      </div>
                      <div className="flex items-end gap-2">
                        <span className={`text-3xl font-bold ${getScoreColor(scores[cat.key] || 0)}`}>{scores[cat.key] || 0}</span>
                        <span className="text-gray-500 text-sm mb-1">/100</span>
                      </div>
                      <div className="mt-3 w-full bg-gray-700/50 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-500 ${
                            (scores[cat.key] || 0) >= 80 ? 'bg-green-500' :
                            (scores[cat.key] || 0) >= 60 ? 'bg-yellow-500' :
                            (scores[cat.key] || 0) >= 40 ? 'bg-orange-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${scores[cat.key] || 0}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Quick Recommendations */}
                <div className="bg-[#111827] border border-gray-700/50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <LightBulbIcon className="w-5 h-5 text-yellow-400" />
                    Top Recommendations
                  </h3>
                  <div className="space-y-3">
                    {recommendations.slice(0, 3).map((rec, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-[#1a2236] rounded-lg">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded border ${getPriorityColor(rec.priority)}`}>
                          {rec.priority}
                        </span>
                        <div>
                          <p className="font-medium text-white text-sm">{rec.title}</p>
                          <p className="text-gray-400 text-sm mt-1">{rec.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Platforms Tab */}
            {activeTab === 'platforms' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(platformScores).map(([platform, data]) => (
                  <div key={platform} className="bg-[#111827] border border-gray-700/50 rounded-xl overflow-hidden">
                    <div className={`bg-gradient-to-r ${platformColors[platform] || 'from-gray-600 to-gray-700'} p-4`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {platformIcons[platform]}
                          <span className="font-semibold capitalize">{platform}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          {data.connected ? (
                            <CheckCircleIcon className="w-5 h-5 text-green-300" />
                          ) : (
                            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-300" />
                          )}
                          <span className="text-sm">{data.connected ? 'Connected' : 'Not Set'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-gray-400 text-sm">Platform Score</span>
                        <span className={`text-2xl font-bold ${getScoreColor(data.score || 0)}`}>{data.score || 0}</span>
                      </div>
                      <div className="w-full bg-gray-700/50 rounded-full h-2 mb-4">
                        <div
                          className={`h-2 rounded-full ${
                            (data.score || 0) >= 80 ? 'bg-green-500' :
                            (data.score || 0) >= 60 ? 'bg-yellow-500' :
                            (data.score || 0) >= 40 ? 'bg-orange-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${data.score || 0}%` }}
                        />
                      </div>
                      {data.tips && data.tips.length > 0 && (
                        <div className="space-y-2">
                          {data.tips.map((tip, i) => (
                            <div key={i} className="flex items-start gap-2 text-sm">
                              <InformationCircleIcon className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-300">{tip}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Recommendations Tab */}
            {activeTab === 'recommendations' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-white">All Recommendations</h3>
                  <div className="flex gap-2 text-xs">
                    <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded border border-red-500/30">
                      High: {recommendations.filter(r => r.priority === 'high').length}
                    </span>
                    <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded border border-yellow-500/30">
                      Medium: {recommendations.filter(r => r.priority === 'medium').length}
                    </span>
                    <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded border border-blue-500/30">
                      Low: {recommendations.filter(r => r.priority === 'low').length}
                    </span>
                  </div>
                </div>
                {recommendations.map((rec, idx) => (
                  <div key={idx} className="bg-[#111827] border border-gray-700/50 rounded-xl p-5">
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-lg ${
                        rec.impact === 'high' ? 'bg-red-500/20' :
                        rec.impact === 'medium' ? 'bg-yellow-500/20' : 'bg-blue-500/20'
                      }`}>
                        {rec.category === 'seoAlignment' ? <MagnifyingGlassIcon className="w-5 h-5 text-purple-400" /> :
                         rec.category === 'contentQuality' ? <StarIcon className="w-5 h-5 text-yellow-400" /> :
                         rec.category === 'engagement' ? <ArrowTrendingUpIcon className="w-5 h-5 text-green-400" /> :
                         <UserGroupIcon className="w-5 h-5 text-blue-400" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-white">{rec.title}</h4>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded border ${getPriorityColor(rec.priority)}`}>
                            {rec.priority}
                          </span>
                        </div>
                        <p className="text-gray-400 text-sm">{rec.description}</p>
                        <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                          <span className="capitalize">Platform: {rec.platform}</span>
                          <span className="capitalize">Impact: {rec.impact}</span>
                          <span className="capitalize">Category: {rec.category}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {recommendations.length === 0 && (
                  <div className="text-center py-12 text-gray-400">
                    <LightBulbIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No recommendations yet. Run an analysis to get started.</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Empty State */}
        {!results && !loading && (
          <div className="text-center py-16 bg-[#111827] border border-gray-700/50 rounded-xl">
            <ShareIcon className="w-16 h-16 mx-auto text-gray-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-300 mb-2">Analyze Your Social Media Presence</h3>
            <p className="text-gray-500 max-w-md mx-auto mb-6">
              Enter your domain and optional social profile URLs above to get a comprehensive analysis of your social media strategy.
            </p>
            <div className="flex justify-center gap-8 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <CheckCircleIcon className="w-4 h-4 text-purple-400" />
                Profile Analysis
              </div>
              <div className="flex items-center gap-2">
                <CheckCircleIcon className="w-4 h-4 text-purple-400" />
                SEO Alignment
              </div>
              <div className="flex items-center gap-2">
                <CheckCircleIcon className="w-4 h-4 text-purple-400" />
                AI Recommendations
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-16 bg-[#111827] border border-gray-700/50 rounded-xl">
            <ArrowPathIcon className="w-12 h-12 mx-auto text-purple-400 animate-spin mb-4" />
            <h3 className="text-xl font-semibold text-gray-300 mb-2">Analyzing Social Media Presence...</h3>
            <p className="text-gray-500">This may take a moment while we evaluate your profiles.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialMediaInsights;
